import { apiGet, apiGetPaginated, apiPost, apiPatch, apiDelete } from './client';
import type {
  Product,
  ProductAccess,
  CreateProductFormData,
  ProductSettings,
  PaginatedResult,
  PaginationInfo,
} from '@/types';

// Transform API response (camelCase) to frontend format (snake_case)
interface ApiProduct {
  id: string;
  organizationId: string;
  organizationName?: string;
  name: string;
  slug: string;
  description: string | null;
  logoUrl: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  settings?: ProductSettings | null;
  clientCount?: number;
  grantCount?: number;
}

interface ApiProductAccess {
  id: string;
  productId: string;
  userId: string;
  userEmail?: string | null;
  userName?: string | null;
  roles: string[];
  permissions: string[];
  grantedAt: string;
  grantedBy?: string | null;
  expiresAt: string | null;
  isActive?: boolean;
}

interface ApiAccessMatrixResult {
  users: AccessMatrixUser[];
  products: ApiProduct[];
  grants: ApiProductAccess[];
  organizations: AccessMatrixOrganization[];
  pagination: PaginationInfo;
}

function transformProduct(p: ApiProduct): Product {
  return {
    id: p.id,
    organization_id: p.organizationId,
    organization_name: p.organizationName,
    name: p.name,
    slug: p.slug,
    description: p.description,
    logo_url: p.logoUrl,
    is_active: p.isActive,
    created_at: p.createdAt,
    updated_at: p.updatedAt || p.createdAt,
    settings: p.settings || null,
    client_count: p.clientCount,
    grant_count: p.grantCount,
  };
}

function transformProductAccess(a: ApiProductAccess): ProductAccess {
  return {
    id: a.id,
    product_id: a.productId,
    user_id: a.userId,
    user_email: a.userEmail ?? null,
    user_name: a.userName ?? null,
    roles: a.roles,
    permissions: a.permissions,
    granted_at: a.grantedAt,
    granted_by: a.grantedBy || null,
    expires_at: a.expiresAt,
    is_active: a.isActive ?? true,
  };
}

export const productsApi = {
  listPage: async (
    query: { organizationId?: string; page?: number; pageSize?: number; search?: string } = {}
  ): Promise<PaginatedResult<Product[]>> => {
    const result = await apiGetPaginated<ApiProduct[]>('/api/products', { params: query });
    return { data: result.data.map(transformProduct), pagination: result.pagination };
  },

  // List products
  list: async (organizationId?: string): Promise<Product[]> => {
    return (await productsApi.listPage({ organizationId, pageSize: 100 })).data;
  },

  // Get product by ID
  get: async (id: string): Promise<Product> => {
    const data = await apiGet<ApiProduct>(`/api/products/${id}`);
    return transformProduct(data);
  },

  // Create product
  create: async (data: CreateProductFormData): Promise<Product> => {
    const result = await apiPost<ApiProduct>('/api/products', {
      organizationId: data.organization_id,
      name: data.name,
      slug: data.slug,
      description: data.description,
      settings: data.settings,
    });
    return transformProduct(result);
  },

  // Update product
  update: async (id: string, data: Partial<CreateProductFormData>): Promise<Product> => {
    const result = await apiPatch<ApiProduct>(`/api/products/${id}`, {
      name: data.name,
      slug: data.slug,
      description: data.description,
      settings: data.settings,
      isActive: 'is_active' in data ? data.is_active : undefined,
    });
    return transformProduct(result);
  },

  // Delete product
  delete: (id: string) => apiDelete<void>(`/api/products/${id}`),

  // Grant access
  grantAccess: (
    productId: string,
    data: { user_id: string; roles?: string[]; permissions?: string[]; expires_at?: string }
  ) =>
    apiPost<ApiProductAccess>(`/api/products/${productId}/access`, {
      userId: data.user_id,
      roles: data.roles,
      permissions: data.permissions,
      expiresAt: data.expires_at,
    }).then(transformProductAccess),

  // Revoke access
  revokeAccess: (productId: string, userId: string) =>
    apiDelete<void>(`/api/products/${productId}/access/${userId}`),

  // Suspend or restore a grant without deleting it
  setAccessActive: (productId: string, userId: string, isActive: boolean) =>
    apiPatch<ApiProductAccess>(`/api/products/${productId}/access/${userId}`, {
      isActive,
    }).then(transformProductAccess),

  // Update roles, permissions, or expiry without changing the grant's status.
  updateAccess: (
    productId: string,
    userId: string,
    data: { roles?: string[]; permissions?: string[]; expires_at?: string | null }
  ) =>
    apiPatch<ApiProductAccess>(`/api/products/${productId}/access/${userId}`, {
      roles: data.roles,
      permissions: data.permissions,
      expiresAt: data.expires_at,
    }).then(transformProductAccess),

  // List access
  listAccess: async (productId: string): Promise<ProductAccess[]> => {
    const data = await apiGet<ApiProductAccess[]>(`/api/products/${productId}/access`);
    return data.map(transformProductAccess);
  },

  // Role catalog (named roles + default permissions) for a product
  listRoles: (productId: string): Promise<ProductRole[]> =>
    apiGet<ProductRole[]>(`/api/products/${productId}/roles`),

  createRole: (productId: string, data: ProductRole): Promise<ProductRole> =>
    apiPost<ProductRole>(`/api/products/${productId}/roles`, data),

  updateRole: (
    productId: string,
    currentName: string,
    data: { name?: string; description?: string; permissions?: string[] }
  ): Promise<ProductRole> =>
    apiPatch<ProductRole>(`/api/products/${productId}/roles/${encodeURIComponent(currentName)}`, data),

  deleteRole: (productId: string, name: string) =>
    apiDelete<void>(`/api/products/${productId}/roles/${encodeURIComponent(name)}`),

  // List the caller's own access grants (apps they are authorized for)
  myAccess: (): Promise<MyAccessGrant[]> => apiGet<MyAccessGrant[]>('/api/products/access/me'),

  // Org memberships and apps reachable without a grant
  myAccessOverview: (): Promise<MyAccessOverview> =>
    apiGet<MyAccessOverview>('/api/products/access/me/overview'),

  // Revoke the caller's own access to a product
  revokeMyAccess: (productId: string) => apiDelete<void>(`/api/products/${productId}/access/me`),

  getAccessMatrix: async (query: AccessMatrixQuery = {}): Promise<AccessMatrixResult> => {
    const result = await apiGet<ApiAccessMatrixResult>('/api/products/access-matrix', {
      params: {
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
        organizationId: query.organizationId,
      },
    });
    return {
      ...result,
      products: result.products.map(transformProduct),
      grants: result.grants.map(transformProductAccess),
    };
  },
};

export interface AccessMatrixQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  organizationId?: string;
}

export interface AccessMatrixUser {
  id: string;
  email: string;
  name: string | null;
  picture: string | null;
  emailVerified: boolean;
  isSuperAdmin: boolean;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  organization: {
    id: string;
    name: string;
    slug: string;
    role: 'owner' | 'admin' | 'member' | 'viewer';
  } | null;
}

export interface AccessMatrixOrganization {
  id: string;
  name: string;
}

export interface AccessMatrixResult {
  users: AccessMatrixUser[];
  products: Product[];
  grants: ProductAccess[];
  organizations: AccessMatrixOrganization[];
  pagination: PaginationInfo;
}

export interface ProductRole {
  name: string;
  description?: string | null;
  permissions: string[];
}

export interface MyAccessGrant {
  productId: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  isActive: boolean;
  organizationName?: string;
  accessModel?: string;
  roles: string[];
  permissions: string[];
  effectivePermissions?: string[];
  grantedAt: string;
  grantedByName?: string | null;
  expiresAt: string | null;
  isGrantActive?: boolean;
}

export interface MyAccessOverview {
  memberships: {
    organizationId: string;
    name: string;
    slug: string;
    role: string;
    joinedAt: string;
  }[];
  available: {
    productId: string;
    name: string;
    slug: string;
    organizationName: string;
    via: 'org_members' | 'open';
    defaultRole: string | null;
  }[];
}
